import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  ListItemAvatar,
  Avatar,
  Tooltip,
  Stack,
  LinearProgress,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { Plan, Workspace } from "../../context/types";
import { useNotification } from "../../context/NotificationContext";
import { updateWorkspaceLimits } from "../../client/api";
import { PLAN_LIMITS, LimitEngine, PlanLimitSet } from "../../constants/limits";
import useStyles from "../CreativeRequest/styles";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 1,
  display: "flex",
  flexDirection: "column",
  maxHeight: "85vh",
};

interface WorkspaceLimitsModalProps {
  workspaces: Workspace[];
  open: boolean;
  onClose: () => void;
  // Saves pricePerUGC / trialCredits via the /plan-and-credits endpoint.
  onSubmit: (
    workspaceId: string,
    data: {
      videoLimit?: number;
      trialCredits?: number;
      trialCreditsVideoAgent?: number;
      trialCreditsPromptToVideo?: number;
      pricePerUGC?: number;
    }
  ) => Promise<void>;
}

type LimitFieldKey =
  | "heygenMonthlyLimit"
  | "videoAgentMonthlyLimit"
  | "promptToVideoMonthlyLimit"
  | "avatarTotalLimit";

const LIMIT_FIELDS: ReadonlyArray<{
  key: LimitFieldKey;
  engine: LimitEngine;
  label: string;
}> = [
  { key: "heygenMonthlyLimit", engine: "heygen", label: "HeyGen videos / month" },
  { key: "videoAgentMonthlyLimit", engine: "videoAgent", label: "Video Agent / month" },
  { key: "promptToVideoMonthlyLimit", engine: "promptToVideo", label: "Prompt-to-video / month" },
  { key: "avatarTotalLimit", engine: "avatars", label: "Avatar total" },
];

type LimitSource = "Plan default" | "Workspace override" | "Full access";

interface ResolvedLimit {
  effective: number; // Infinity allowed
  source: LimitSource;
  hasOverride: boolean;
}

const resolvePlanDefaults = (workspace: Workspace): PlanLimitSet => {
  if (workspace.category === "fullaccess") return PLAN_LIMITS.fullAccess;
  const plan = workspace.plan;
  if (!plan) return PLAN_LIMITS.free;
  if (plan.isCustom) return PLAN_LIMITS.customDefault;
  if (plan.name === "starter") return PLAN_LIMITS.starter;
  if (plan.name === "pro") return PLAN_LIMITS.pro;
  return PLAN_LIMITS.customDefault;
};

const resolveLimit = (
  workspace: Workspace,
  field: LimitFieldKey,
  engine: LimitEngine
): ResolvedLimit => {
  const planDefaults = resolvePlanDefaults(workspace);
  const planDefault = planDefaults[engine];

  if (workspace.category === "fullaccess") {
    return { effective: planDefault, source: "Full access", hasOverride: false };
  }

  const planOverride = workspace.plan?.[field];
  if (typeof planOverride === "number") {
    return { effective: planOverride, source: "Workspace override", hasOverride: true };
  }

  return { effective: planDefault, source: "Plan default", hasOverride: false };
};

const formatDefaultDisplay = (value: number): string =>
  value === Infinity ? "Unlimited" : `${value}`;

const resolveUsage = (
  workspace: Workspace,
  engine: LimitEngine
): number => {
  switch (engine) {
    case "heygen":
      return workspace.monthlyGeneratedVideos ?? 0;
    case "videoAgent":
      return workspace.monthlyVideoAgentVideos ?? 0;
    case "promptToVideo":
      return workspace.monthlyPromptToVideoVideos ?? 0;
    case "avatars":
      return workspace.avatarCount ?? 0;
    default:
      return 0;
  }
};

// Engines whose usage rolls over monthly. Avatars are a live count instead.
const isMonthlyEngine = (engine: LimitEngine): boolean => engine !== "avatars";

const getProgressColor = (
  ratio: number
): "primary" | "warning" | "error" => {
  if (ratio >= 1) return "error";
  if (ratio >= 0.75) return "warning";
  return "primary";
};

export const WorkspaceLimitsModal: React.FC<WorkspaceLimitsModalProps> = ({
  workspaces,
  open,
  onClose,
  onSubmit,
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  // Existing fields (plan + credits)
  const [videoLimit, setVideoLimit] = useState<number>(0);
  const [trialCredits, setTrialCredits] = useState<number>(0);
  const [trialCreditsVideoAgent, setTrialCreditsVideoAgent] = useState<number>(0);
  const [trialCreditsPromptToVideo, setTrialCreditsPromptToVideo] = useState<number>(0);
  const [pricePerUGC, setPricePerUgc] = useState<number>(0);
  // New per-engine limit field inputs. Empty string => "no change, use default".
  const [limitInputs, setLimitInputs] = useState<Record<LimitFieldKey, string>>({
    heygenMonthlyLimit: "",
    videoAgentMonthlyLimit: "",
    promptToVideoMonthlyLimit: "",
    avatarTotalLimit: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useStyles();
  const { addNotification } = useNotification();

  // Update form fields when workspace selection changes
  React.useEffect(() => {
    if (selectedWorkspace) {
      setVideoLimit(selectedWorkspace.plan?.videoLimit || 0);
      setTrialCredits(selectedWorkspace.trialCredits || 0);
      setTrialCreditsVideoAgent(selectedWorkspace.trialCreditsVideoAgent || 0);
      setTrialCreditsPromptToVideo(selectedWorkspace.trialCreditsPromptToVideo || 0);
      setPricePerUgc(selectedWorkspace.plan?.pricePerUGC || 0);

      // Pre-fill the input ONLY if an override already exists. Otherwise the
      // field stays empty so the placeholder (default value) shows through.
      const plan: Plan | undefined = selectedWorkspace.plan;
      setLimitInputs({
        heygenMonthlyLimit:
          typeof plan?.heygenMonthlyLimit === "number"
            ? String(plan.heygenMonthlyLimit)
            : "",
        videoAgentMonthlyLimit:
          typeof plan?.videoAgentMonthlyLimit === "number"
            ? String(plan.videoAgentMonthlyLimit)
            : "",
        promptToVideoMonthlyLimit:
          typeof plan?.promptToVideoMonthlyLimit === "number"
            ? String(plan.promptToVideoMonthlyLimit)
            : "",
        avatarTotalLimit:
          typeof plan?.avatarTotalLimit === "number"
            ? String(plan.avatarTotalLimit)
            : "",
      });
    }
  }, [selectedWorkspace]);

  const isFullAccess = selectedWorkspace?.category === "fullaccess";

  const handleSubmit = async () => {
    if (!selectedWorkspace) return;

    setIsSubmitting(true);
    try {
      // 1) Build the limits payload — only include fields the admin actually
      //    typed a value into. Skip fields equal to the resolved default that
      //    have no existing override (preserves "still using plan default").
      const limitsPayload: {
        heygenMonthlyLimit?: number;
        videoAgentMonthlyLimit?: number;
        promptToVideoMonthlyLimit?: number;
        avatarTotalLimit?: number;
      } = {};

      LIMIT_FIELDS.forEach(({ key, engine }) => {
        const raw = limitInputs[key];
        if (raw === "") return; // not changed
        const parsed = Number(raw);
        if (!Number.isFinite(parsed) || parsed < 0) return;

        const resolved = resolveLimit(selectedWorkspace, key, engine);
        // Only send if it differs from what's currently effective. If admin
        // typed the same number that's already the override or default, no-op.
        if (parsed !== resolved.effective) {
          limitsPayload[key] = parsed;
        } else if (resolved.hasOverride) {
          // Same as current override — still no-op (no diff).
        }
      });

      const hasLimitChanges = Object.keys(limitsPayload).length > 0;

      // 2) Build the plan-and-credits payload.
      const planPayload: {
        videoLimit?: number;
        trialCredits?: number;
        trialCreditsVideoAgent?: number;
        trialCreditsPromptToVideo?: number;
        pricePerUGC?: number;
      } = {};

      if (selectedWorkspace.plan) {
        if (videoLimit !== (selectedWorkspace.plan.videoLimit || 0)) {
          planPayload.videoLimit = videoLimit;
        }
        if (pricePerUGC !== (selectedWorkspace.plan.pricePerUGC || 0)) {
          planPayload.pricePerUGC = pricePerUGC;
        }
      }
      if (trialCredits !== (selectedWorkspace.trialCredits || 0)) {
        planPayload.trialCredits = trialCredits;
      }
      if (
        trialCreditsVideoAgent !==
        (selectedWorkspace.trialCreditsVideoAgent || 0)
      ) {
        planPayload.trialCreditsVideoAgent = trialCreditsVideoAgent;
      }
      if (
        trialCreditsPromptToVideo !==
        (selectedWorkspace.trialCreditsPromptToVideo || 0)
      ) {
        planPayload.trialCreditsPromptToVideo = trialCreditsPromptToVideo;
      }

      const hasPlanChanges = Object.keys(planPayload).length > 0;

      if (!hasLimitChanges && !hasPlanChanges) {
        addNotification("No changes to save", "info");
        return;
      }

      if (hasLimitChanges) {
        await updateWorkspaceLimits(selectedWorkspace._id, limitsPayload);
      }
      if (hasPlanChanges) {
        await onSubmit(selectedWorkspace._id, planPayload);
      }

      addNotification("Workspace limits updated!", "success");
    } catch (error) {
      console.error("Error updating workspace limits:", error);
      addNotification(
        "Unable to update workspace data, please try again or contact support",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" gutterBottom>
          Manage Workspace Limits
        </Typography>

        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", gap: 2 }}>
          {/* Workspace List */}
          <Box sx={{ width: "35%", overflowY: "auto" }}>
            <List dense>
              {workspaces.map((workspace) => {
                const isFullAccessWs = workspace.category === "fullaccess";
                const hasPlan = !!workspace.plan;
                const editable = hasPlan || isFullAccessWs;
                const listItem = (
                  <ListItem
                    key={workspace._id}
                    onClick={() => editable && setSelectedWorkspace(workspace)}
                    sx={{
                      mb: 1,
                      backgroundColor:
                        selectedWorkspace?._id === workspace._id
                          ? "action.selected"
                          : undefined,
                      cursor: editable ? "pointer" : "not-allowed",
                      opacity: editable ? 1 : 0.6,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: workspace.plan
                            ? "secondary.main"
                            : undefined,
                        }}
                      >
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={workspace.name.toUpperCase()}
                      secondary={
                        isFullAccessWs ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label="Full Access"
                              size="small"
                              color="warning"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          </Box>
                        ) : workspace.plan ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`Plan: ${workspace.plan.name.toLocaleUpperCase()}`}
                              size="small"
                              color="secondary"
                              sx={{ mr: 1, mb: 1 }}
                            />
                            <Chip
                              label={`$${workspace.plan.pricePerUGC}/UGC`}
                              size="small"
                            />
                          </Box>
                        ) : workspace.trialCredits ? (
                          <Tooltip title="Coming soon: give trial credits to specific workspaces">
                            <Chip
                              label={`${workspace.trialCredits} Trial Credits`}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Tooltip>
                        ) : null
                      }
                    />
                  </ListItem>
                );

                return editable ? (
                  listItem
                ) : (
                  <Tooltip
                    key={workspace._id}
                    title="Coming soon: give trial credits to specific workspaces"
                    placement="left"
                  >
                    {listItem}
                  </Tooltip>
                );
              })}
            </List>
          </Box>

          {/* Workspace Limits Form */}
          <Box sx={{ width: "65%", overflowY: "auto", pr: 1 }}>
            {selectedWorkspace ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedWorkspace.name.toUpperCase()}
                </Typography>

                {selectedWorkspace.plan && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Changing these values will convert this workspace to a
                    custom plan.
                  </Alert>
                )}

                {isFullAccess && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This workspace has Full Access — per-engine limits are
                    unlimited (prompt-to-video capped at 500) and not editable
                    here.
                  </Alert>
                )}

                {selectedWorkspace.plan && (
                  <>
                    <TextField
                      label="Price per UGC ($)"
                      type="number"
                      value={pricePerUGC}
                      onChange={(e) => setPricePerUgc(Number(e.target.value))}
                      fullWidth
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Cost per generated video"
                      slotProps={{
                        inputLabel: {
                          classes: { root: classes.inputLabel },
                          shrink: true,
                        },
                      }}
                    />
                    <TextField
                      label="Legacy HeyGen Video Limit"
                      type="number"
                      value={videoLimit}
                      onChange={(e) => setVideoLimit(Number(e.target.value))}
                      fullWidth
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0 }}
                      helperText="Legacy HeyGen monthly cap (kept for backwards compat)"
                      slotProps={{
                        inputLabel: {
                          classes: { root: classes.inputLabel },
                          shrink: true,
                        },
                      }}
                    />
                  </>
                )}

                {/* New per-engine limits */}
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {LIMIT_FIELDS.map(({ key, engine, label }) => {
                    const resolved = resolveLimit(
                      selectedWorkspace,
                      key,
                      engine
                    );
                    const placeholder = formatDefaultDisplay(resolved.effective);
                    const chipColor: "default" | "primary" | "warning" =
                      resolved.source === "Workspace override"
                        ? "primary"
                        : resolved.source === "Full access"
                        ? "warning"
                        : "default";

                    // Usage line under the input.
                    const used = resolveUsage(selectedWorkspace, engine);
                    const limit = resolved.effective;
                    const isUnlimited = limit === Infinity;
                    const usagePrefix = isMonthlyEngine(engine)
                      ? "Used this month"
                      : "Total active";
                    const usageLabel = isUnlimited
                      ? `${usagePrefix}: ${used} / Unlimited`
                      : `${usagePrefix}: ${used} / ${limit}`;
                    // Guard against divide-by-zero and Infinity. Cap at 100.
                    const ratio =
                      !isUnlimited && limit > 0 ? used / limit : 0;
                    const progressValue = Math.min(ratio * 100, 100);
                    const progressColor = getProgressColor(ratio);

                    return (
                      <Box key={key}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {label}
                          </Typography>
                          <Chip
                            label={resolved.source}
                            size="small"
                            color={chipColor}
                            variant={
                              resolved.source === "Plan default"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </Box>
                        {isFullAccess ? (
                          <TextField
                            value=""
                            placeholder={placeholder}
                            fullWidth
                            disabled
                            helperText={
                              resolved.effective === Infinity
                                ? "Unlimited"
                                : `Default for Full Access: ${placeholder}`
                            }
                            slotProps={{
                              inputLabel: {
                                classes: { root: classes.inputLabel },
                                shrink: true,
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            type="number"
                            value={limitInputs[key]}
                            onChange={(e) =>
                              setLimitInputs((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                            placeholder={`${placeholder} (default)`}
                            fullWidth
                            inputProps={{ min: 0 }}
                            helperText={
                              resolved.hasOverride
                                ? `Current override: ${placeholder}. Clear to leave as override; type a new value to change.`
                                : `Leave blank to use the ${resolved.source.toLowerCase()} (${placeholder}).`
                            }
                            slotProps={{
                              inputLabel: {
                                classes: { root: classes.inputLabel },
                                shrink: true,
                              },
                            }}
                          />
                        )}
                        <Box sx={{ mt: 0.5, px: 0.25 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.4 }}
                          >
                            {usageLabel}
                          </Typography>
                          {!isUnlimited && (
                            <LinearProgress
                              variant="determinate"
                              value={progressValue}
                              color={progressColor}
                              sx={{
                                mt: 0.5,
                                height: 4,
                                borderRadius: 2,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Each engine has its own bucket. 0 = unlimited for that engine.
                </Typography>

                <TextField
                  label="Trial Credits — HeyGen / Avatar Videos"
                  type="number"
                  value={trialCredits}
                  onChange={(e) => setTrialCredits(Number(e.target.value))}
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0 }}
                  helperText="Number of free trial HeyGen / avatar video generations"
                  slotProps={{
                    inputLabel: {
                      classes: { root: classes.inputLabel },
                      shrink: true,
                    },
                  }}
                />

                <TextField
                  label="Trial Credits — Video Agent"
                  type="number"
                  value={trialCreditsVideoAgent}
                  onChange={(e) =>
                    setTrialCreditsVideoAgent(Number(e.target.value))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0 }}
                  helperText="Number of free trial Video Agent generations"
                  slotProps={{
                    inputLabel: {
                      classes: { root: classes.inputLabel },
                      shrink: true,
                    },
                  }}
                />

                <TextField
                  label="Trial Credits — Prompt to Video"
                  type="number"
                  value={trialCreditsPromptToVideo}
                  onChange={(e) =>
                    setTrialCreditsPromptToVideo(Number(e.target.value))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0 }}
                  helperText="Number of free trial Prompt-to-Video generations"
                  slotProps={{
                    inputLabel: {
                      classes: { root: classes.inputLabel },
                      shrink: true,
                    },
                  }}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography color="text.secondary">
                  Select a workspace to edit
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
